import React, { useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Save, AlertTriangle } from 'lucide-react';
import '../styles/ClienteForm.css';
import { useAuth } from '../context/AuthContext';
const IDENTIDAD = new Set(['rfc', 'curp', 'identificacionPersonal', 'identificacionEmpresa']);

// Un solo lugar define los pasos, sus campos y sus etiquetas. Alta y edición
// comparten esta definición: no existe un segundo formulario duplicado.
export const PASOS = [
  {
    titulo: 'Datos',
    encabezado: 'Datos personales',
    campos: [
      { name: 'nombres', label: 'Nombres', required: true, placeholder: 'Juan' },
      { name: 'apellidoPaterno', label: 'Apellido paterno', required: true, placeholder: 'Pérez' },
      { name: 'apellidoMaterno', label: 'Apellido materno', placeholder: 'Hernández' },
      { name: 'telefonoPrincipal', label: 'Teléfono principal', required: true, type: 'tel', placeholder: '+52 5512345678' },
      { name: 'telefonoAlterno', label: 'Teléfono alterno', type: 'tel', placeholder: 'Opcional' },
      { name: 'email', label: 'Correo electrónico', type: 'email', placeholder: 'Opcional', ayuda: 'No es obligatorio.' }
    ]
  },
  {
    titulo: 'Identificación',
    encabezado: 'Identificación',
    campos: [
      {
        name: 'tipoPersona',
        label: 'Tipo de persona',
        required: true,
        tipo: 'select',
        opciones: [
          { value: 'PERSONA_FISICA', label: 'Persona física' },
          { value: 'PERSONA_MORAL', label: 'Persona moral' }
        ]
      },
      { name: 'rfc', label: 'RFC', placeholder: 'PEHJ800101AB1' },
      { name: 'curp', label: 'CURP', placeholder: 'PEHJ800101HDFRRN09' },
      { name: 'identificacionPersonal', label: 'Identificación personal', placeholder: 'INE, pasaporte…' },
      { name: 'nacionalidad', label: 'Nacionalidad', placeholder: 'Mexicana' }
    ]
  },
  {
    titulo: 'Domicilio',
    encabezado: 'Domicilio',
    campos: [
      { name: 'calle', label: 'Calle', placeholder: 'Av. Principal', ancho: 'full' },
      { name: 'numeroExterior', label: 'Número exterior', placeholder: '123' },
      { name: 'numeroInterior', label: 'Número interior', placeholder: 'Opcional' },
      { name: 'colonia', label: 'Colonia', placeholder: 'Centro' },
      { name: 'codigoPostal', label: 'Código postal', placeholder: '06000' },
      { name: 'localidad', label: 'Localidad', placeholder: 'Opcional' },
      { name: 'municipio', label: 'Municipio', placeholder: 'Cuauhtémoc' },
      { name: 'estadoDomicilio', label: 'Estado', placeholder: 'Ciudad de México' },
      { name: 'pais', label: 'País', placeholder: 'MEXICO' }
    ]
  },
  {
    titulo: 'Actividad',
    encabezado: 'Actividad e información adicional',
    campos: [
      { name: 'actividadEmpresarial', label: 'Actividad empresarial', placeholder: 'Comercio al por menor', ancho: 'full' },
      { name: 'identificacionEmpresa', label: 'Identificación de la empresa', placeholder: 'Opcional' }
    ]
  },
  {
    titulo: 'Contabilidad',
    encabezado: 'Información contable',
    ayudaPaso:
      'Estas cuentas se conservan por compatibilidad con el MIDAS anterior. Todavía no alimentan ningún cálculo.',
    campos: [
      { name: 'cuentaContableCapital', label: 'Cuenta contable capital', placeholder: '1103-001' },
      { name: 'cuentaContableInteres', label: 'Cuenta contable interés', placeholder: '4101-001' }
    ]
  },
  { titulo: 'Confirmar', encabezado: 'Revisión y confirmación', revision: true }
];

const VACIO = {
  nombres: '', apellidoPaterno: '', apellidoMaterno: '',
  telefonoPrincipal: '', telefonoAlterno: '', email: '',
  tipoPersona: 'PERSONA_FISICA', rfc: '', curp: '',
  identificacionPersonal: '', nacionalidad: '',
  calle: '', numeroExterior: '', numeroInterior: '', colonia: '',
  codigoPostal: '', localidad: '', municipio: '', estadoDomicilio: '',
  pais: 'MEXICO',
  actividadEmpresarial: '', identificacionEmpresa: '',
  cuentaContableCapital: '', cuentaContableInteres: ''
};

// La API entrega el domicilio anidado; el formulario trabaja plano.
export function aFormulario(cliente) {
  if (!cliente) return { ...VACIO };
  const d = cliente.domicilio || {};
  const v = (x) => x ?? '';
  return {
    ...VACIO,
    nombres: v(cliente.nombres),
    apellidoPaterno: v(cliente.apellidoPaterno),
    apellidoMaterno: v(cliente.apellidoMaterno),
    telefonoPrincipal: v(cliente.telefonoPrincipal),
    telefonoAlterno: v(cliente.telefonoAlterno),
    email: v(cliente.email),
    tipoPersona: cliente.tipoPersona || 'PERSONA_FISICA',
    rfc: v(cliente.rfc),
    curp: v(cliente.curp),
    identificacionPersonal: v(cliente.identificacionPersonal),
    nacionalidad: v(cliente.nacionalidad),
    calle: v(d.calle),
    numeroExterior: v(d.numeroExterior),
    numeroInterior: v(d.numeroInterior),
    colonia: v(d.colonia),
    codigoPostal: v(d.codigoPostal),
    localidad: v(d.localidad),
    municipio: v(d.municipio),
    estadoDomicilio: v(d.estado),
    pais: v(d.pais) || 'MEXICO',
    actividadEmpresarial: v(cliente.actividadEmpresarial),
    identificacionEmpresa: v(cliente.identificacionEmpresa),
    cuentaContableCapital: v(cliente.cuentaContableCapital),
    cuentaContableInteres: v(cliente.cuentaContableInteres)
  };
}

// Validación de presencia en el navegador. La validación real, incluidos
// los formatos, vive en el backend; esta solo evita un viaje inútil.
const OBLIGATORIOS = {
  nombres: 'Los nombres son obligatorios',
  apellidoPaterno: 'El apellido paterno es obligatorio',
  telefonoPrincipal: 'El teléfono principal es obligatorio'
};

function ClienteForm({ valorInicial, modo, onSubmit, onCancelar, enviando, erroresServidor }) {
  const { hasPermission } = useAuth();
  const identidadRestringida = valorInicial?.identidadOculta || !hasPermission('EXPEDIENTES_VER');
  const [paso, setPaso] = useState(0);
  const [datos, setDatos] = useState(() => aFormulario(valorInicial));
  const [erroresLocales, setErroresLocales] = useState({});

  // Los errores del backend llegan como [{campo, mensaje}] y se muestran
  // junto a su campo, no en un alert.
  const erroresApi = useMemo(() => {
    const m = {};
    (erroresServidor || []).forEach((e) => {
      m[e.campo] = e.mensaje;
    });
    return m;
  }, [erroresServidor]);

  const errorDe = (name) => erroresLocales[name] || erroresApi[name] || null;

  const cambiar = (name, valor) => {
    setDatos((prev) => ({ ...prev, [name]: valor }));
    if (erroresLocales[name]) {
      setErroresLocales((prev) => {
        const s = { ...prev };
        delete s[name];
        return s;
      });
    }
  };

  const validarPaso = (indice) => {
    const campos = PASOS[indice].campos || [];
    const fallos = {};
    campos.forEach((c) => {
      if (OBLIGATORIOS[c.name] && !String(datos[c.name] || '').trim()) {
        fallos[c.name] = OBLIGATORIOS[c.name];
      }
    });
    setErroresLocales(fallos);
    return Object.keys(fallos).length === 0;
  };

  const avanzar = () => {
    if (!validarPaso(paso)) return;
    setPaso((p) => Math.min(p + 1, PASOS.length - 1));
  };

  const retroceder = () => setPaso((p) => Math.max(p - 1, 0));

  // Solo se permite saltar a un paso ya visitado o al inmediato siguiente.
  const irAPaso = (destino) => {
    if (destino <= paso) return setPaso(destino);
    if (destino === paso + 1) return avanzar();
  };

  const enviar = () => {
    for (let i = 0; i < PASOS.length; i += 1) {
      if (!validarPaso(i)) {
        setPaso(i);
        return;
      }
    }
    const payload = { ...datos };
    if (identidadRestringida) IDENTIDAD.forEach(campo => delete payload[campo]);
    onSubmit(payload);
  };

  const pasoActual = PASOS[paso];
  const esUltimo = paso === PASOS.length - 1;

  return (
    <div className="cliente-form">
      <ol className="pasos" aria-label="Progreso del formulario">
        {PASOS.map((p, i) => (
          <li key={p.titulo} className={`paso ${i === paso ? 'is-actual' : ''} ${i < paso ? 'is-hecho' : ''}`}>
            <button type="button" className="paso-btn" onClick={() => irAPaso(i)} disabled={i > paso + 1}>
              <span className="paso-num">{i < paso ? <Check size={13} strokeWidth={2.5} /> : i + 1}</span>
              <span className="paso-titulo">{p.titulo}</span>
            </button>
          </li>
        ))}
      </ol>

      <div className="form-panel">
        <div className="form-panel-head">
          <h2>{pasoActual.encabezado}</h2>
          {pasoActual.ayudaPaso && <p className="form-panel-ayuda">{pasoActual.ayudaPaso}</p>}
        </div>

        {pasoActual.revision ? (
          <Revision datos={datos} identidadRestringida={identidadRestringida} />
        ) : (
          <div className="form-grid">
            {pasoActual.campos.filter(c => !identidadRestringida || !IDENTIDAD.has(c.name)).map((c) => {
              const error = errorDe(c.name);
              return (
                <div key={c.name} className={`campo ${c.ancho === 'full' ? 'es-ancho' : ''} ${error ? 'tiene-error' : ''}`}>
                  <label htmlFor={c.name}>
                    {c.label}
                    {c.required && <span className="campo-req" aria-hidden="true"> *</span>}
                  </label>

                  {c.tipo === 'select' ? (
                    <select id={c.name} value={datos[c.name]} onChange={(e) => cambiar(c.name, e.target.value)}>
                      {c.opciones.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={c.name}
                      type={c.type || 'text'}
                      value={datos[c.name]}
                      placeholder={c.placeholder}
                      onChange={(e) => cambiar(c.name, e.target.value)}
                      aria-invalid={!!error}
                      aria-describedby={error ? `${c.name}-error` : undefined}
                    />
                  )}

                  {error ? (
                    <p className="campo-error" id={`${c.name}-error`}>
                      <AlertTriangle size={13} strokeWidth={2} />
                      {error}
                    </p>
                  ) : (
                    c.ayuda && <p className="campo-ayuda">{c.ayuda}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="form-acciones">
        <button type="button" className="btn btn-ghost" onClick={onCancelar} disabled={enviando}>
          Cancelar
        </button>

        <div className="form-acciones-derecha">
          {paso > 0 && (
            <button type="button" className="btn btn-secondary" onClick={retroceder} disabled={enviando}>
              <ChevronLeft size={16} strokeWidth={2} />
              Anterior
            </button>
          )}

          {esUltimo ? (
            <button type="button" className="btn btn-primary" onClick={enviar} disabled={enviando}>
              <Save size={16} strokeWidth={2} />
              {enviando ? 'Guardando…' : modo === 'editar' ? 'Guardar cambios' : 'Crear cliente'}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={avanzar} disabled={enviando}>
              Continuar
              <ChevronRight size={16} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const sinDato = <span className="cell-muted">—</span>;

function Bloque({ titulo, filas }) {
  return (
    <section className="revision-bloque">
      <h3>{titulo}</h3>
      <dl>
        {filas.map(([etiqueta, valor]) => (
          <div key={etiqueta}>
            <dt>{etiqueta}</dt>
            <dd>{valor || sinDato}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Revision({ datos, identidadRestringida }) {
  const nombreCompleto = [datos.nombres, datos.apellidoPaterno, datos.apellidoMaterno]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="revision">
      <Bloque
        titulo="Datos personales"
        filas={[
          ['Nombre completo', nombreCompleto],
          ['Teléfono principal', datos.telefonoPrincipal],
          ['Teléfono alterno', datos.telefonoAlterno],
          ['Correo', datos.email]
        ]}
      />
      <Bloque
        titulo="Identificación"
        filas={[
          ['Tipo de persona', datos.tipoPersona === 'PERSONA_MORAL' ? 'Persona moral' : 'Persona física'],
          ['RFC', identidadRestringida ? 'Acceso restringido' : datos.rfc],
          ['CURP', identidadRestringida ? 'Acceso restringido' : datos.curp],
          ['Identificación', identidadRestringida ? 'Acceso restringido' : datos.identificacionPersonal],
          ['Nacionalidad', datos.nacionalidad]
        ]}
      />
      <Bloque
        titulo="Domicilio"
        filas={[
          ['Calle y número', [datos.calle, datos.numeroExterior, datos.numeroInterior && `Int. ${datos.numeroInterior}`].filter(Boolean).join(' ')],
          ['Colonia', datos.colonia],
          ['Código postal', datos.codigoPostal],
          ['Localidad', datos.localidad],
          ['Municipio', datos.municipio],
          ['Estado', datos.estadoDomicilio],
          ['País', datos.pais]
        ]}
      />
      <Bloque
        titulo="Actividad"
        filas={[
          ['Actividad empresarial', datos.actividadEmpresarial],
          ['Identificación de empresa', identidadRestringida ? 'Acceso restringido' : datos.identificacionEmpresa]
        ]}
      />
      <Bloque
        titulo="Información contable"
        filas={[
          ['Cuenta capital', datos.cuentaContableCapital],
          ['Cuenta interés', datos.cuentaContableInteres]
        ]}
      />
    </div>
  );
}

export default ClienteForm;
